/**
 * Get 2-letter initials from a name or email.
 * "David Cohen" → "DC"
 * "david.cohen@test.com" → "DC"
 * "david" → "DA"
 */
export function getInitials(nameOrEmail: string): string {
  if (!nameOrEmail) return '??';

  // If it's an email, use the part before @
  const name = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;

  // Split by spaces, dots, underscores, hyphens
  const parts = name.split(/[\s._-]+/).filter(Boolean);

  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  // Single word — take first 2 chars
  return name.substring(0, 2).toUpperCase();
}

/**
 * First initial of the first name only — used for the user's own avatar
 * in the sidebar rail.
 * "Rena Bessner" → "R"
 * "rena.bessner@test.com" → "R"
 * Falls back to "?" for empty input rather than "??", since a
 * single-character avatar reads as a glyph and "??" would look like a bug.
 */
export function getFirstInitial(nameOrEmail: string): string {
  if (!nameOrEmail) return '?';
  const name = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
  const first = name.split(/[\s._-]+/).filter(Boolean)[0] || name;
  return first.charAt(0).toUpperCase();
}

/**
 * Title-case the first letter of each word so usernames stored lowercase
 * (or pulled from an email's local-part) read cleanly in greetings and
 * the sidebar.  "rena bessner" → "Rena Bessner".
 */
export function capitalizeName(nameOrEmail: string): string {
  if (!nameOrEmail) return '';
  const name = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
  return name
    .split(/(\s+)/)
    .map(part => (/^\s+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join('');
}
